<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue'
import 'swiper/css'

import { EntityOrganizationLogo } from '~/components/entity/organization-logo'
import {
  buildCurrencyFilter,
  buildDepositsSort,
  buildPeriodFilter,
  type DepositsQueryParam
} from '~/components/widget/deposits/list-meili-search/filters'
import { searchAliases } from './mocks'
import type {
  ISearchAlias,
  ISearchAliasCard,
  ISearchAliasHit,
  ISearchAliasOrganization
} from './types'

const config = useRuntimeConfig()
const route = useRoute()
const { locale } = useI18n()

// Two logos keep the title and the logo row on a single line down to the
// narrowest slide width, where a third one would truncate the longest label.
const MAX_VISIBLE_LOGOS = 2

// Period aliases compare rates across every organization at once, so they must
// stay within one currency: foreign-currency rates run far above the som ones
// and would win a comparison that means nothing to the reader. Som is the
// default the deposits list opens on.
const KGS_CURRENCY_ID = 1

// Shown as a chip on those cards. A currency code is the same in both locales,
// so it stays out of the translation files.
const KGS_CURRENCY_CODE = 'KGS'

// Only the fields the cards actually read; deposit documents carry long
// descriptions and nested types in two locales that would be fetched for nothing.
const RETRIEVED_FIELDS = ['rates', 'currencies', 'organization']

const activeValue = (param: DepositsQueryParam) => String(route.query[param] || '')

// Clicking the active alias clears the filter, so cards behave like toggles.
// route.path already carries the locale prefix, so the link stays on the current page.
const buildAliasLink = (param: DepositsQueryParam, value: string) => ({
  path: route.path,
  query: {
    ...route.query,
    page: undefined,
    [param]: activeValue(param) === value ? undefined : value
  }
})

// The client is used directly rather than through useMeiliSearch: the aliases
// need one multi-search round trip instead of six, and that composable also
// writes every response into a state ref shared with the deposits list.
const meiliClient = useMeiliSearchRef()
const depositsIndex = `${config.public.meiliEnvironment}_deposits`

/**
 * Each alias kind knows how to narrow the index to its own offers and which of a
 * deposit's rates may drive the headline number, so neither is branched on at
 * the call sites. Both mirror the deposits list, keeping a card's figures equal
 * to what the user meets after following it.
 */
const ALIAS_KINDS: Record<
  DepositsQueryParam,
  {
    filter: (value: string) => string
    sort: (value: string) => string[]
    matches: (hit: ISearchAliasHit, value: string) => ISearchAliasHit['rates']
    /** Whether the rate needs a currency chip. Only period cards do — a currency
     * card already names it in its own title, where repeating it would read
     * as "Лучшие в сомах / ставка до 16% / KGS". */
    showCurrencyChip: boolean
  }
> = {
  period: {
    filter: (value) => buildPeriodFilter(value, KGS_CURRENCY_ID),
    sort: () => buildDepositsSort('kgs'),
    showCurrencyChip: true,
    matches: (hit, value) =>
      (hit.rates || []).filter(
        (rate) =>
          rate.currency_id === KGS_CURRENCY_ID &&
          rate.period_from <= Number(value) &&
          rate.period >= Number(value)
      )
  },
  currency: {
    filter: buildCurrencyFilter,
    // The list switches to the per-currency rate field once a currency is picked.
    sort: (value) => buildDepositsSort(value),
    showCurrencyChip: false,
    matches: (hit, value) => {
      // Resolved per hit rather than from the currency store, which app.vue fills
      // asynchronously and may still be empty while this widget renders.
      const currencyId = hit.currencies?.find((item) => item.code.toLowerCase() === value)?.id

      return (hit.rates || []).filter((rate) => rate.currency_id === currencyId)
    }
  }
}

const collectAliasCard = (alias: ISearchAlias, hits: ISearchAliasHit[]) => {
  const { matches, showCurrencyChip } = ALIAS_KINDS[alias.param]

  let maxRate: number | null = null
  // Hits arrive in the list's order, which puts promoted offers first — that says
  // nothing about their rate. Score every organization, then rank by rate below,
  // so the logo row shows who actually offers the best conditions.
  const bestByOrganization = new Map<number, ISearchAliasOrganization & { rate: number }>()

  hits.forEach((hit) => {
    const matchingRates = matches(hit, alias.value) || []

    if (!matchingRates.length) return

    const hitBestRate = Math.max(...matchingRates.map((rate) => rate.rate))

    if (maxRate === null || hitBestRate > maxRate) {
      maxRate = hitBestRate
    }

    if (!hit.organization.logo) return

    // One organization may hold several deposits, so keep its best rate only.
    const listed = bestByOrganization.get(hit.organization.id)

    if (!listed || hitBestRate > listed.rate) {
      bestByOrganization.set(hit.organization.id, {
        id: hit.organization.id,
        title: hit.organization.title,
        logo: hit.organization.logo,
        rate: hitBestRate
      })
    }
  })

  const organizations: ISearchAliasCard['organizations'] = [...bestByOrganization.values()]
    .sort((a, b) => b.rate - a.rate)
    .slice(0, MAX_VISIBLE_LOGOS)
    .map(({ rate, ...organization }) => organization)

  return { ...alias, maxRate, organizations, showCurrencyChip }
}

const { data: aliasCards } = await useAsyncData(
  'deposits-search-aliases',
  async () => {
    const { results } = await meiliClient.multiSearch({
      queries: searchAliases.map((alias) => {
        const { filter, sort } = ALIAS_KINDS[alias.param]

        return {
          indexUid: depositsIndex,
          q: '',
          filter: [filter(alias.value)],
          sort: sort(alias.value),
          attributesToRetrieve: RETRIEVED_FIELDS,
          limit: 200
        }
      })
    })

    // Results come back in the order the queries were sent.
    return searchAliases.map((alias, index) =>
      collectAliasCard(alias, (results[index]?.hits || []) as ISearchAliasHit[])
    )
  },
  {
    // Rendered above the deposits list, so it must not hold up the page it decorates.
    lazy: true,
    // The cards are the same on every deposit type — their queries never look at
    // the section. Switching sections remounts this widget, so without reusing
    // the payload it would refetch identical data on each navigation.
    getCachedData: (key, nuxtApp) => nuxtApp.payload.data[key] ?? nuxtApp.static.data[key]
  }
)
</script>

<template>
  <section v-if="aliasCards?.length" class="mb-6">
    <Swiper :space-between="16" :slides-per-view="'auto'">
      <SwiperSlide
        v-for="card in aliasCards"
        :key="`${card.param}-${card.value}`"
        class="!w-[85%] sm:!w-[47%] lg:!w-[31%]"
      >
        <article
          class="PrimaryCard !gap-3 !p-4"
          :class="{ '!border-blue-600': activeValue(card.param) === card.value }"
        >
          <header class="flex items-center justify-between gap-2">
            <Heading tag="h3" class="h6 mb-0 min-w-0 flex-1">
              <NuxtLink
                :to="buildAliasLink(card.param, card.value)"
                class="block truncate after:absolute after:inset-0"
              >
                {{ $t(card.label) }}
              </NuxtLink>
            </Heading>

            <ul v-if="card.organizations.length" class="flex flex-shrink-0 items-center gap-1">
              <li v-for="organization in card.organizations" :key="organization.id">
                <EntityOrganizationLogo
                  :src="organization.logo"
                  :alt="organization.title[locale] || ''"
                  size="xs"
                />
              </li>
            </ul>
          </header>

          <div v-if="card.maxRate !== null" class="flex flex-wrap items-center gap-1">
            <Badge variant="gray" class="w-fit">
              {{ $t('widget.deposits_search_aliases.up_to_rate', { rate: useRateFormat(card.maxRate) }) }}
            </Badge>

            <Badge v-if="card.showCurrencyChip" variant="blue" class="w-fit">
              {{ KGS_CURRENCY_CODE }}
            </Badge>
          </div>
        </article>
      </SwiperSlide>
    </Swiper>
  </section>
</template>
